import { Delete as DeleteIcon } from "@mui/icons-material";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import MultiSelectDropdown from "./MultiSelectDropdown";
import VirtualizedCompanyDropdown, {
  CompanyOption,
} from "./VirtualizedCompanyDropdown";

export interface RelationshipFilterData {
  id: string;
  relationshipTypes: string[];
  targetCompanies: CompanyOption[];
  maxTiers: number;
}

interface RelationshipFilterProps {
  filter: RelationshipFilterData;
  onUpdate: (updatedFilter: RelationshipFilterData) => void;
  onRemove: () => void;
}

// Semantic relationship types from the perspective of the searched organization
// These map to actual Kuzu database relationships with proper directionality in the backend
const RELATIONSHIP_TYPES = [
  // Ownership relationships - from searched org perspective
  { value: "is_owned_by", label: "Is owned by" },
  { value: "owns", label: "Owns" },
  { value: "has_majority_owner", label: "Has majority owner" },
  { value: "is_majority_owner_of", label: "Is majority owner of" },
  { value: "has_partial_owner", label: "Has partial owner" },
  { value: "is_partial_owner_of", label: "Is partial owner of" },
  { value: "has_minority_investor", label: "Has minority investor" },
  { value: "is_minority_investor_in", label: "Is minority investor in" },
  { value: "is_controlled_by", label: "Is controlled by" },
  { value: "controls", label: "Controls" },

  // Supply chain relationships - from searched org perspective
  { value: "supplies_to", label: "Supplies to" },
  { value: "is_supplied_by", label: "Is supplied by" },
  { value: "facility_supplies_to", label: "Facility supplies to" },
  { value: "facility_is_supplied_by", label: "Facility is supplied by" },

  // Partnership relationships - bidirectional
  { value: "has_partnership_with", label: "Has partnership with" },
  { value: "has_joint_venture_with", label: "Has joint venture with" },
  {
    value: "has_joint_venture_subsidiary_with",
    label: "Has joint venture subsidiary with",
  },
  { value: "merged_with", label: "Merged with" },

  // Construction and facility relationships
  { value: "constructs_facility_for", label: "Constructs facility for" },
  {
    value: "has_facility_constructed_by",
    label: "Has facility constructed by",
  },
  { value: "has_sub_facility", label: "Has sub-facility" },
  { value: "is_sub_facility_of", label: "Is sub-facility of" },

  // Project relationships
  { value: "has_project", label: "Has project" },
  { value: "facility_has_project", label: "Facility has project" },

  // Asset relationships
  { value: "acquired_assets_from", label: "Acquired assets from" },
  { value: "sold_assets_to", label: "Sold assets to" },

  // Conflict relationships
  { value: "has_conflict_with_org", label: "Has conflict with organization" },
  {
    value: "has_conflict_with_facility",
    label: "Has conflict with facility",
  },
  // { value: "facility_has_conflict", label: "Facility has conflict" },

  // Operations relationships
  // { value: "operates_at_facility", label: "Operates at facility" },
  { value: "has_operator", label: "Has operator" },

  // // Facility transformation relationships
  // { value: "facility_moved_to", label: "Facility moved to" },
  // { value: "facility_moved_from", label: "Facility moved from" },
  // { value: "facility_repurposed_to", label: "Facility repurposed to" },
  // { value: "facility_repurposed_from", label: "Facility repurposed from" },

  // Investigation and regulatory relationships
  { value: "investigates", label: "Investigates" },
  { value: "is_investigated_by", label: "Is investigated by" },
  { value: "regulates", label: "Regulates" },
  { value: "is_regulated_by", label: "Is regulated by" },
];

const TIER_OPTIONS = [
  { value: 1, label: "Tier 1 (Direct)" },
  { value: 2, label: "Tier 2" },
  { value: 3, label: "Tier 3" },
  { value: 4, label: "Tier 4" },
  { value: 5, label: "Tier 5" },
];

const RelationshipFilter: React.FC<RelationshipFilterProps> = ({
  filter,
  onUpdate,
  onRemove,
}) => {
  const handleRelationshipTypesChange = (types: string[]) => {
    onUpdate({
      ...filter,
      relationshipTypes: types,
    });
  };

  const handleCompaniesChange = (companies: CompanyOption[]) => {
    onUpdate({
      ...filter,
      targetCompanies: companies,
    });
  };

  const handleTierChange = (event: SelectChangeEvent<number>) => {
    onUpdate({
      ...filter,
      maxTiers: event.target.value as number,
    });
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 2,
        mb: 1,
        backgroundColor: "background.paper",
        position: "relative",
      }}
    >
      {/* Remove button */}
      <IconButton
        onClick={onRemove}
        size="small"
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          color: "text.secondary",
          "&:hover": {
            color: "error.main",
            backgroundColor: "error.light",
          },
        }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>

      <Stack spacing={2}>
        {/* Filter title */}
        <Typography variant="subtitle2" fontWeight="medium" sx={{ pr: 4 }}>
          Relationship Filter
        </Typography>

        {/* Relationship types selection */}
        <MultiSelectDropdown
          label="Relationship Types"
          value={filter.relationshipTypes}
          options={RELATIONSHIP_TYPES.map((type) => type.value)}
          onChange={handleRelationshipTypesChange}
          getChipLabel={(value) => {
            const config = RELATIONSHIP_TYPES.find((t) => t.value === value);
            return config?.label || value;
          }}
          getOptionLabel={(value) => {
            const config = RELATIONSHIP_TYPES.find((t) => t.value === value);
            return config?.label || value;
          }}
          searchable
        />

        {/* Target companies selection */}
        <VirtualizedCompanyDropdown
          label="Target Companies"
          value={filter.targetCompanies}
          onChange={handleCompaniesChange}
          placeholder="Search for companies..."
          multiple={true}
        />

        {/* Tier selection */}
        <FormControl size="small" fullWidth>
          <InputLabel>Maximum Tiers</InputLabel>
          <Select
            value={filter.maxTiers}
            onChange={handleTierChange}
            label="Maximum Tiers"
          >
            {TIER_OPTIONS.map((tier) => (
              <MenuItem key={tier.value} value={tier.value}>
                {tier.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Summary display */}
        {filter.relationshipTypes.length > 0 &&
          filter.targetCompanies.length > 0 && (
            <Box
              sx={{
                backgroundColor: "action.hover",
                borderRadius: 1,
                p: 1.5,
                mt: 1,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: "medium" }}
              >
                Filter Summary:
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Find any organization that{" "}
                <strong>
                  {filter.relationshipTypes
                    .map((type) => {
                      const config = RELATIONSHIP_TYPES.find(
                        (t) => t.value === type
                      );
                      return config?.label.toLowerCase() || type;
                    })
                    .join(" OR ")}
                </strong>{" "}
                {filter.targetCompanies
                  .map((company) => company.name)
                  .join(", ")}{" "}
                (up to{" "}
                {filter.maxTiers === 99
                  ? "any tier"
                  : `tier ${filter.maxTiers}`}
                )
              </Typography>
            </Box>
          )}
      </Stack>
    </Box>
  );
};

export default RelationshipFilter;
